import { PaginateUtil, tickerUtil } from "../utils";
import {
  MATCH_FILTER_KEYS,
  MATCH_FILTER_NAMES,
  STAT_LEVEL_CLASSES,
  SORT_ORDER,
  SORT_TYPES,
  SIGNAL_FILTER_NAMES,
} from "../utils/constant";

function filterBySearch(matches, filterValue) {
  if (!filterValue) return matches;

  return matches.filter(({ home, away, league }) =>
    [home.name, away.name, league].some((name = "") =>
      name.toLowerCase().includes(filterValue.toLowerCase()),
    ),
  );
}

function livefilterMatches(matches, filter, filterValue) {
  switch (filter) {
    case MATCH_FILTER_KEYS.ALL:
      return matches;
    case MATCH_FILTER_KEYS.FAVORITES:
      return matches.filter((match) => match.isFavorite);
    case MATCH_FILTER_KEYS.SEARCH:
      return filterBySearch(matches, filterValue);
    case MATCH_FILTER_KEYS.SIGNALS:
      return filterBySignals(matches, filterValue);
    default:
      return matches;
  }
}

function filterBySignals(matches, filterValue) {
  if (filterValue === false) {
    return matches;
  }
  return matches.filter((match) => match.liveSignals.length);
}

function filterList(matches, filters, opts) {
  return filters.reduce((acc, filter) => {
    if (typeof filter === "string" && !opts.noTabFilter) {
      return livefilterMatches(acc, filter);
    } else if (typeof filter === "object" && !opts.noSignalFilter) {
      return livefilterMatches(acc, filter.type, filter.value);
    } else {
      return acc;
    }
  }, matches);
}

function sortList(matches, sort, favorite) {
  const { type, order } = sort;

  return [...matches].sort((a, b) => {
    let itemA = favorite.includes(a.id);
    let itemB = favorite.includes(b.id);

    if (itemA && !itemB) {
      return -1;
    } else if (!itemA && itemB) {
      return 1;
    } else {
      switch (type) {
        case SORT_TYPES.TIME:
          itemA = a.time;
          itemB = b.time;
          break;
        case SORT_TYPES.STATS:
          itemA = a.totalLevel;
          itemB = b.totalLevel;
          break;
        default:
          return 0;
      }

      if (itemA === itemB) return 0;
      const sortOrder = itemA > itemB ? -1 : 1;
      if (order === SORT_ORDER.ASC) return sortOrder * -1;
      return sortOrder;
    }
  });
}

function mapLiveSignals({ home, away, liveSignals, time, ended, part }) {
  const score = home.score + away.score;
  const firstTimeScore = home.score1time + away.score1time;

  return liveSignals.map(({ over, gamePeriod, odds }) => {
    if (
      (over <= firstTimeScore && gamePeriod == 1) ||
      (over <= score && gamePeriod == 2)
    ) {
      return { over, gamePeriod, odds, signalClass: "has-background-success" };
    }
    if (
      (over > firstTimeScore && gamePeriod == 1 && part == "FT") ||
      (over > score && ended)
    ) {
      return { over, gamePeriod, odds, signalClass: "has-background-danger" };
    }
    return { over, gamePeriod, odds, signalClass: "has-background-warning" };
  });
}

function getLevelClass(level, type = STAT_LEVEL_CLASSES.TIME) {
  const color = getLevelColor(Number(level));
  return color ? `${type}-${color}` : "";
}

function getLevelColor(level) {
  switch (level) {
    case 1:
      return "success";
    case 2:
      return "warning";
    case 3:
      return "danger";
    default:
      return "";
  }
}

function liveOdds(odds) {
  if (odds && odds.FT && odds.FThdp && odds.FThdp[0] && odds.FT[0]) {
    const FThdp = odds.FThdp[0];
    const FT = odds.FT[0];
    return {
      FThdp: {
        over:
          !Number.isNaN(FThdp.over) && FThdp.over !== null ? FThdp.over : "-",
        total:
          !Number.isNaN(FThdp.total) && FThdp.total !== null
            ? FThdp.total
            : "-",
        under:
          !Number.isNaN(FThdp.under) && FThdp.under !== null
            ? FThdp.under
            : "-",
      },
      FT: {
        over: !Number.isNaN(FT.over) && FT.over !== null ? FT.over : "-",
        total: !Number.isNaN(FT.total) && FT.total !== null ? FT.total : "-",
        under: !Number.isNaN(FT.under) && FT.under !== null ? FT.under : "-",
      },
    };
  }

  return {
    FThdp: { over: "-", total: "-", under: "-" },
    FT: { over: "-", total: "-", under: "-" },
  };
}

function mapLiveList(matches, state) {
  return matches.map((match) => {
    const { home, away } = match;
    const companyOdds = (match.data || [{}]).find((odd) => odd.companyId === 8);
    return {
      ...match,
      isFavorite: state.favorites.includes(match.id),
      mini: state.liveExpanded.includes(match.id) === false,
      odds: liveOdds(companyOdds),
      liveSignals: mapLiveSignals(match),
      firstTime:
        match.time > 45
          ? { home: home.score1time, away: away.score1time }
          : false,
      totalLevel: parseInt(home.level) + parseInt(away.level),
      $homeLevel: getLevelClass(home.level, STAT_LEVEL_CLASSES.TEAM),
      $awayLevel: getLevelClass(away.level, STAT_LEVEL_CLASSES.TEAM),
      $timeLevel: getLevelClass(match.timeLevel),
    };
  });
}

export default {
  lastMatches: (state, getters) => state.match.lastMatches,

  liveFilters: (state, getters) => (filters) => {
    const opts = { noTabFilter: true, noSignalFilter: true, noSearch: true };
    const [filterKey] = state.match.filters;
    const matches = getters.liveMatches(opts).data;

    const result = filters.map((key) => ({
      key,
      name: MATCH_FILTER_NAMES[key],
      isActive: filterKey === key,
      count: livefilterMatches(matches, key).length,
    }));
    return result;
  },

  liveSorting: (state, getters) => (sorting) => {
    const { type, order } = state.match.sort;

    return sorting.map((key) => ({
      key,
      name: key,
      isActive: type === key,
      isASC: order === SORT_ORDER.ASC,
      isDESC: order === SORT_ORDER.DESC,
    }));
  },

  liveMatches: (state) => (opts) => {
    const { list, searchText, filters, sort, favorites } = state.match;

    const mappedList = mapLiveList(list, state.match);
    const searchedList = filterBySearch(
      mappedList,
      !opts.noSearch ? searchText : "",
    );

    const filteredList = filterList(searchedList, filters, opts);
    const matchList = sortList(filteredList, sort, favorites);

    const total = matchList.length;

    if (opts.page && opts.perPage) {
      return {
        data: PaginateUtil.paginateList(matchList, opts.page, opts.perPage),
        total,
      };
    }

    return { data: matchList, total };
  },

  tikerMatches: (state) => (opts) => {
    const { list, searchText, filters, sort, favorites } = state.match;

    const mappedList = tickerUtil.mapTikerList(list, state.match);
    const filteredList = tickerUtil.filterList(mappedList, filters, opts);
    const searchedList = tickerUtil.filterBySearch(
      filteredList,
      !opts.noSearch ? searchText : "",
    );
    const matchList = tickerUtil.sortList(searchedList, sort, favorites);

    const total = matchList.length;

    if (opts.page && opts.perPage) {
      return {
        data: PaginateUtil.paginateList(matchList, opts.page, opts.perPage),
        total,
      };
    }

    return {
      data: matchList,
      total,
    };
  },

  tickerFiltres: (state, getters) => (filters) => {
    const opts = { noTabFilter: true, noSignalFilter: true, noSearch: true };
    const matches = getters.tikerMatches(opts).data;
    const [filterKey] = state.match.filters;

    const result = filters.map((key) => ({
      key,
      name: MATCH_FILTER_NAMES[key],
      isActive: filterKey === key,
      count: tickerUtil.filterMatches(matches, key).length,
    }));

    return result;
  },

  tickerSignalFiltres: (state, getters) => (filters) => {
    const filterKey = state.match.filters.find(
      ({ type }) => type === MATCH_FILTER_KEYS.SIGNAL_TYPES,
    );

    const opts = { noTabFilter: true, noSignalFilter: true };
    const matches = getters.tikerMatches(opts).data;

    const matchCount = tickerUtil.matchCountBySignals(matches, filters);

    return filters.map((key) => ({
      key,
      name: SIGNAL_FILTER_NAMES[key],
      isActive: filterKey.value.has(key),
      count: matchCount[key],
    }));
  },

  tickerSorting: (state, getters) => (sorting) => {
    const { type, order } = state.match.sort;

    return sorting.map((key) => ({
      key,
      name: key,
      isActive: type === key,
      isASC: order === SORT_ORDER.ASC,
      isDESC: order === SORT_ORDER.DESC,
    }));
  },
};
